// Flagged picks tile on Dispatch > Flagged (per Alex, 2026-10-08).
//
// One card per task a leader is fetching from high racking, with a
// countdown to when its order is expected at the pack desk — the time by
// which the unit must be on the staging shelf. Colours follow the Flagged
// page's age tiers: grey with more than 30 minutes to go, amber under 30,
// red under 10 or overdue, green once shelved. The ETA is the BE's
// first-cut model (services/flagged-picks.js) and is re-read every 30 s.
//
// Leader actions: Cancel pick (the order ships without the unit; a picked
// unit becomes a put-back) and, when the packer flagged the order while
// waiting and the unit is now shelved, Return to dispatch.
import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, Flag, PackageSearch, Timer, Undo2, CheckCircle, Send, XCircle } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../../i18n";
import { apiFetch } from "../../api/apiFetch";
import FlowLoader from "../FlowLoader";
import type { FlaggedPickTask } from "../../types/Dispatch";
import { displayItemCode } from "./item-code";

export type CountdownTier = "fresh" | "warn" | "urgent" | "done";

// Minutes to the ETA decide the tier; a shelved task is done whatever the
// clock says, and a task with no estimate reads as fresh.
export const countdownTier = (task: Pick<FlaggedPickTask, "status" | "etaAt">, now: number): CountdownTier => {
    if (task.status === "staged") return "done";
    if (!task.etaAt) return "fresh";
    const minutes = (Date.parse(task.etaAt) - now) / 60_000;
    if (!Number.isFinite(minutes)) return "fresh";
    if (minutes <= 10) return "urgent";
    if (minutes <= 30) return "warn";
    return "fresh";
};

export const formatMinutes = (totalMinutes: number): string => {
    const m = Math.max(0, Math.round(totalMinutes));
    if (m < 1) return "<1m";
    if (m < 60) return `${m}m`;
    return `${Math.floor(m / 60)}h ${m % 60}m`;
};

// Local wall-clock "14:32" for an ETA timestamp.
export const formatClock = (ms: number): string => {
    const d = new Date(ms);
    if (!Number.isFinite(d.getTime())) return "";
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const sortTasks = (tasks: FlaggedPickTask[]): FlaggedPickTask[] => {
    const rank = (x: FlaggedPickTask) => x.status === "put_back" ? 3 : x.status === "staged" ? 2 : 1;
    return [...tasks].sort((a, b) => rank(a) - rank(b)
        || (a.etaAt ? Date.parse(a.etaAt) : Number.MAX_SAFE_INTEGER) - (b.etaAt ? Date.parse(b.etaAt) : Number.MAX_SAFE_INTEGER)
        || Date.parse(a.createdAt) - Date.parse(b.createdAt));
};

type Props = {
    warehouse: string;
    canWrite: boolean;
    onBack: () => void;
    /** Called after a change that affects the flagged-orders list too (a
     *  Return to dispatch), so the host page can refresh its own list. */
    onOrdersChanged?: () => void;
    pollMs?: number;
};

const FlaggedPicksPanel = ({ warehouse, canWrite, onBack, onOrdersChanged, pollMs = 30_000 }: Props) => {
    const { t } = useTranslation();
    const [tasks, setTasks] = useState<FlaggedPickTask[]>([]);
    const [stats, setStats] = useState<{ openCount: number; putBackCount: number; avgFlagToShelfSeconds: number | null } | null>(null);
    const [loading, setLoading] = useState(true);
    // false outside a Flagged Picks warehouse (Poland): no high-racking
    // walk there, so the tile is the found-items shelf (Alex, 2026-10-20).
    const [available, setAvailable] = useState(true);
    const [now, setNow] = useState(() => Date.now());
    const [armed, setArmed] = useState<number | null>(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            if (warehouse) params.set("warehouse", warehouse);
            const res = await apiFetch(`/flagged-picks/list?${params}`, {});
            if (!res.ok) return;
            const data = await res.json();
            setTasks(Array.isArray(data.tasks) ? data.tasks : []);
            setStats(data.stats ?? null);
            setAvailable(data.available !== false);
            setNow(Date.now());
        } catch {
            toast.error(t("server_error"), { toastId: "fp-panel-load" });
        } finally {
            setLoading(false);
        }
    }, [t, warehouse]);

    useEffect(() => {
        load();
        const poll = window.setInterval(load, pollMs);
        const tick = window.setInterval(() => setNow(Date.now()), 15_000);
        return () => { window.clearInterval(poll); window.clearInterval(tick); };
    }, [load, pollMs]);

    const post = async (path: string, body: Record<string, unknown>) => {
        setBusy(true);
        try {
            const res = await apiFetch(path, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
            const data = await res.json().catch(() => null);
            if (!res.ok || !data) { toast.error(data?.message || t("server_error"), { toastId: "fp-panel-err" }); return null; }
            return data;
        } catch {
            toast.error(t("server_error"), { toastId: "fp-panel-err" });
            return null;
        } finally {
            setBusy(false);
        }
    };

    const cancelTask = async (task: FlaggedPickTask) => {
        if (armed !== task.id) { setArmed(task.id); return; }
        setArmed(null);
        const data = await post(`/flagged-picks/cancel`, { taskId: task.id });
        if (data) await load();
    };

    const releaseOrder = async (task: FlaggedPickTask) => {
        const data = await post(`/flagged-picks/release-order`, { orderId: task.orderId });
        if (!data) return;
        if (data.valid === false) {
            toast.error(data.reason === "not_staged" ? t("fp_release_not_staged") : t("server_error"), { toastId: "fp-panel-err" });
            return;
        }
        toast.success(t("fp_release_done", { order: task.orderNumber }), { toastId: "fp-panel-ok" });
        await load();
        onOrdersChanged?.();
    };

    // The countdown is the headline; the clock time under it ("due ~14:32")
    // is what a leader actually plans around (2026-10-09).
    const countdown = (task: FlaggedPickTask) => {
        const shelf = task.stagingDisplay ?? task.stagingLocation ?? "";
        if (task.status === "staged") return { value: shelf, label: t("fp_status_staged", { location: shelf }) };
        if (task.status === "put_back") return { value: "", label: t("fp_status_put_back", { location: shelf }) };
        // Waiting for a put-away: no walk is possible yet, so the ETA has
        // nothing to count down to. The hold itself is the status chip.
        if (task.status === "awaiting_stock") return { value: "—", label: t("fp_eta_after_put_away") };
        if (!task.etaAt) return { value: "—", label: t("fp_eta_unknown") };
        const etaMs = Date.parse(task.etaAt);
        const minutes = (etaMs - now) / 60_000;
        if (task.etaBasis === "at_desk" || minutes <= 0.5) {
            const late = Math.max(0, -minutes);
            return { value: late < 1 ? "0m" : formatMinutes(late), label: task.etaBasis === "at_desk" && late < 1 ? t("fp_eta_now") : t("fp_eta_overdue", { time: formatMinutes(late) }) };
        }
        return { value: formatMinutes(minutes), label: t("fp_eta_due_at", { time: formatClock(etaMs) }) };
    };

    const releasedOrders = new Set<string>();
    // Found-items view: only what is on a shelf now (shelf + order + item).
    // Put-backs are cleared on the Found items page's Book in pill.
    const foundView = !available;
    // Found units that need someone (book back in, resolve in Flagged, then
    // ship on its own by the soonest deadline) come first; the ones waiting
    // for the packer last.
    const SHELF_RANK: Record<string, number> = { book_back_in: 0, resolve: 1, ship: 2 };
    const shelfRank = (x: FlaggedPickTask) => SHELF_RANK[x.shelfAction?.action ?? ""] ?? 3;
    const sorted = foundView
        ? sortTasks(tasks.filter(x => x.status === "staged")).sort((a, b) => shelfRank(a) - shelfRank(b)
            || (a.shelfAction?.shipBy ? Date.parse(a.shelfAction.shipBy) : 0) - (b.shelfAction?.shipBy ? Date.parse(b.shelfAction.shipBy) : 0))
        : sortTasks(tasks);

    return (
        <div className={`fp-tile${foundView ? " fp-tile--found" : ""}`} data-testid="fp-panel" data-view={foundView ? "found" : "picks"}>
            <div className="fp-tile-head">
                <h2>{foundView ? t("flagged_found_tile") : t("flagged_picks_tile")}</h2>
                <button type="button" className="btn btn-ghost dispatch-modal-btn" onClick={onBack} data-testid="fp-panel-back">
                    <ArrowLeft size={14} />
                    {t("fp_back_to_orders")}
                </button>
            </div>

            {foundView ? (
            <div className="dispatch-flagged-kpis">
                <div className="dispatch-flagged-kpi dispatch-flagged-kpi--open">
                    <span className="dispatch-flagged-kpi-icon"><PackageSearch size={16} strokeWidth={2.5} /></span>
                    <span className="dispatch-flagged-kpi-label">{t("fp_kpi_on_shelf")}</span>
                    <span className="dispatch-flagged-kpi-value" data-testid="fp-kpi-on-shelf">{loading && tasks.length === 0 ? "—" : sorted.length}</span>
                </div>
            </div>
            ) : (
            <div className="dispatch-flagged-kpis">
                <div className="dispatch-flagged-kpi dispatch-flagged-kpi--open">
                    <span className="dispatch-flagged-kpi-icon"><PackageSearch size={16} strokeWidth={2.5} /></span>
                    <span className="dispatch-flagged-kpi-label">{t("fp_kpi_open")}</span>
                    <span className="dispatch-flagged-kpi-value" data-testid="fp-kpi-open">{stats?.openCount ?? "—"}</span>
                </div>
                <div className="dispatch-flagged-kpi">
                    <span className="dispatch-flagged-kpi-icon"><Timer size={16} strokeWidth={2.5} /></span>
                    <span className="dispatch-flagged-kpi-label">{t("fp_kpi_shelf_time")}</span>
                    <span className="dispatch-flagged-kpi-value">
                        {stats?.avgFlagToShelfSeconds == null ? "—" : formatMinutes(stats.avgFlagToShelfSeconds / 60)}
                    </span>
                </div>
                <div className="dispatch-flagged-kpi">
                    <span className="dispatch-flagged-kpi-icon"><Undo2 size={16} strokeWidth={2.5} /></span>
                    <span className="dispatch-flagged-kpi-label">{t("fp_kpi_put_back")}</span>
                    <span className="dispatch-flagged-kpi-value">{stats?.putBackCount ?? "—"}</span>
                </div>
            </div>
            )}

            {loading && tasks.length === 0 ? (
                <div className="dispatch-flagged-empty"><FlowLoader message={t("loading")} /></div>
            ) : sorted.length === 0 ? (
                <div className="dispatch-flagged-empty">
                    <CheckCircle size={48} strokeWidth={1.5} />
                    <p>{foundView ? t("fp_found_empty") : t("fp_empty")}</p>
                </div>
            ) : (
                <div className="fp-tile-list">
                    {sorted.map(task => {
                        const tier: CountdownTier = task.shelfAction?.action === "book_back_in" ? "urgent"
                            : task.shelfAction?.action === "ship" || task.shelfAction?.action === "resolve" ? "warn"
                            : countdownTier(task, now);
                        const cd = countdown(task);
                        const canRelease = canWrite && task.status === "staged" && !!task.awaitingFlag && !releasedOrders.has(task.orderId);
                        if (canRelease) releasedOrders.add(task.orderId);
                        const hold = task.status === "awaiting_stock" ? (task.hold?.[0] ?? null) : null;
                        const statusText = task.status === "awaiting_stock"
                            ? (hold
                                ? t("fp_status_awaiting_stock", {
                                    module: t(hold.module === "shelf" ? "fp_hold_module_shelf" : "fp_hold_module_forecast"),
                                    tote: hold.toteId,
                                    name: hold.pickerName ?? "",
                                })
                                : t("fp_status_awaiting_stock_unknown"))
                            : task.status === "pending" ? t("fp_status_pending")
                            : task.status === "picking" ? t("fp_status_picking", { name: task.pickerName ?? "" })
                            : task.status === "staged" ? t("fp_status_staged", { location: task.stagingDisplay ?? task.stagingLocation ?? "" })
                            : t("fp_status_put_back", { location: task.stagingDisplay ?? task.stagingLocation ?? "" });
                        return (
                            <div key={task.id} className={`fp-tile-card fp-tile-card--${task.status === "put_back" ? "urgent" : tier}`} data-testid="fp-tile-card" data-tier={tier} data-status={task.status}>
                                {task.thumbnailUrl || task.imageUrl
                                    ? <img className="fp-tile-img" src={task.thumbnailUrl ?? task.imageUrl ?? ""} alt="" />
                                    : <div className="fp-tile-img fp-task-img--empty"><PackageSearch size={18} /></div>}
                                <div className="fp-tile-main">
                                    <div className="fp-tile-order">{t("order_title_label")} #{task.orderNumber}</div>
                                    <div className="fp-tile-title">{task.title}</div>
                                    <div className="fp-tile-ids">
                                        {displayItemCode(task.sku, task.barcode)} · x{task.qty}
                                        {task.origin !== "found_item" && <> · {task.sourceDisplay ? t("fp_from_location", { location: task.sourceDisplay }) : t("fp_no_shelf_yet")}</>}
                                    </div>
                                </div>
                                <div className="fp-tile-status">
                                    <span className={`fp-tile-chip fp-tile-chip--${task.status}`}>{statusText}</span>
                                    {task.shelfAction && (
                                        <span className={`fp-tile-chip fp-tile-chip--shelf-${task.shelfAction.action}`} data-testid="fp-shelf-action" data-action={task.shelfAction.action}>
                                            {task.shelfAction.action === "ship" && task.shelfAction.shipBy
                                                ? t("fp_shelf_ship_by", { time: formatClock(Date.parse(task.shelfAction.shipBy)) })
                                                : t(`fp_shelf_${task.shelfAction.action}`)}
                                        </span>
                                    )}
                                    <span className="fp-tile-meta">{t(`fp_origin_${task.origin}`)}</span>
                                    {task.awaitingFlag && (
                                        <span className="fp-tile-meta"><Flag size={11} /> {t("fp_awaiting_flag", { name: task.awaitingFlag.flaggedBy ?? "" })}</span>
                                    )}
                                </div>
                                <div className="fp-tile-side">
                                    {task.status !== "put_back" && (
                                        <span className="fp-tile-countdown-wrap">
                                            <span className={`fp-countdown fp-countdown--${tier}`} data-testid="fp-countdown">{cd.value}</span>
                                            <span className="fp-countdown-label">{cd.label}</span>
                                        </span>
                                    )}
                                    {canWrite && (
                                        <div className="fp-tile-actions">
                                            {canRelease && (
                                                <button type="button" className="btn btn-primary dispatch-modal-btn" onClick={() => releaseOrder(task)} disabled={busy} data-testid="fp-release">
                                                    <Send size={14} />{t("fp_release_order")}
                                                </button>
                                            )}
                                            {(task.status === "awaiting_stock" || task.status === "pending" || task.status === "picking" || task.status === "staged") && (
                                                <button type="button" className={`btn ${armed === task.id ? "btn-danger" : "btn-ghost"} dispatch-modal-btn`} onClick={() => cancelTask(task)} disabled={busy} data-testid="fp-cancel" title={t("fp_cancel_confirm")}>
                                                    <XCircle size={14} />{armed === task.id ? t("confirm") : t("fp_cancel")}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default FlaggedPicksPanel;
